import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/context/AppContext';
import api, { errMsg } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TagCombobox } from '@/components/TagCombobox';
import { toast } from 'sonner';
import { Loader2, Pencil } from 'lucide-react';

const STATUSES = ['AVAILABLE', 'SOLD', 'IN_TRANSIT', 'RETURNED'];

/**
 * Full single-item edit dialog. Edits every field of a garment.
 * props: open, onOpenChange, garment (object), onDone()
 */
export default function ItemEditDialog({ open, onOpenChange, garment, onDone }) {
  const qc = useQueryClient();
  const { stores } = useApp();
  const [saving, setSaving] = useState(false);
  const [v, setV] = useState({});

  useEffect(() => {
    if (open && garment) {
      setV({
        barcode: garment.barcode || '',
        product: garment.product || '',
        category: garment.category || '',
        size: garment.size || '',
        colour: garment.colour || '',
        price: garment.price != null ? String(garment.price) : '',
        cost: garment.cost != null ? String(garment.cost) : '',
        store_id: garment.store_id || '',
        status: garment.status || 'AVAILABLE',
      });
    }
  }, [open, garment]);

  const { data: cats = [] } = useQuery({ queryKey: ['categories'], queryFn: async () => (await api.get('/categories')).data.data, enabled: open });

  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const save = async () => {
    const bc = (v.barcode || '').trim().toUpperCase();
    if (!bc) { toast.error('Barcode/item code is required.'); return; }
    if (!(Number(v.price) > 0)) { toast.error('Enter a valid selling price.'); return; }
    if (v.cost !== '' && !(Number(v.cost) >= 0)) { toast.error('Enter a valid cost.'); return; }
    const payload = {
      barcode: bc,
      product: v.product.trim(),
      category: v.category.trim(),
      size: v.size.trim(),
      colour: v.colour.trim(),
      price: Number(v.price),
      cost: v.cost === '' ? null : Number(v.cost),
      store_id: v.store_id,
      status: v.status,
    };
    setSaving(true);
    try {
      await api.patch(`/garments/${encodeURIComponent(garment.barcode)}`, payload);
      toast.success('Item updated');
      qc.invalidateQueries({ queryKey: ['garments'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      qc.invalidateQueries({ queryKey: ['categories'] });
      qc.invalidateQueries({ queryKey: ['garment'] });
      onDone?.(bc);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(x) => { if (!saving) onOpenChange(x); }}>
      <DialogContent className="max-w-lg" data-testid="item-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-primary" /> Edit Item</DialogTitle>
          <DialogDescription>Change any detail of this garment. Changing the store or status updates stock instantly.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Item Code / Barcode" className="col-span-2">
            <Input value={v.barcode || ''} onChange={(e) => set('barcode', e.target.value)} className="h-10 rounded-lg font-mono" data-testid="itemedit-barcode" />
          </Field>
          <Field label="Item Name" className="col-span-2">
            <Input value={v.product || ''} onChange={(e) => set('product', e.target.value)} className="h-10 rounded-lg" data-testid="itemedit-product" placeholder="Item name" />
          </Field>
          <Field label="Category" className="col-span-2">
            <TagCombobox value={v.category || ''} onChange={(val) => set('category', val)} placeholder="Select / create" testId="itemedit-category" className="h-10" />
          </Field>
          <Field label="Size">
            <Input value={v.size || ''} onChange={(e) => set('size', e.target.value)} className="h-10 rounded-lg" data-testid="itemedit-size" placeholder="-" />
          </Field>
          <Field label="Colour">
            <Input value={v.colour || ''} onChange={(e) => set('colour', e.target.value)} className="h-10 rounded-lg" data-testid="itemedit-colour" placeholder="-" />
          </Field>
          <Field label="Selling Price">
            <Input inputMode="numeric" value={v.price || ''} onChange={(e) => set('price', e.target.value)} className="h-10 rounded-lg" data-testid="itemedit-price" placeholder="0" />
          </Field>
          <Field label="Purchase Price (cost)">
            <Input inputMode="numeric" value={v.cost || ''} onChange={(e) => set('cost', e.target.value)} className="h-10 rounded-lg" data-testid="itemedit-cost" placeholder="0" />
          </Field>
          <Field label="Store">
            <Select value={v.store_id} onValueChange={(val) => set('store_id', val)}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="itemedit-store"><SelectValue /></SelectTrigger>
              <SelectContent>
                {stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={v.status} onValueChange={(val) => set('status', val)}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="itemedit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="secondary" className="rounded-lg" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button className="rounded-lg" onClick={save} disabled={saving} data-testid="itemedit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
